import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Leaf, Lock, ArrowLeft, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) return setError("Password minimal 6 karakter.");
    if (password !== confirm) return setError("Konfirmasi password tidak cocok.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password berhasil direset");
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
        <div className="flex items-center gap-2.5 mb-6">
          <div className="h-10 w-10 rounded-lg bg-green-700 flex items-center justify-center">
            <Leaf className="h-5 w-5 text-white" />
          </div>
          <div className="font-heading font-bold text-slate-900">SIA-HPP GREENCON</div>
        </div>

        {!token ? (
          <div className="text-center py-4">
            <AlertCircle className="h-12 w-12 text-amber-500 mx-auto" />
            <h1 className="font-heading text-xl font-bold text-slate-900 mt-4">Token Tidak Ditemukan</h1>
            <p className="text-sm text-slate-500 mt-2">Tautan reset tidak valid. Silakan minta tautan baru dari halaman Lupa Password.</p>
            <Link to="/forgot-password"><Button variant="outline" className="mt-6">Minta Tautan Baru</Button></Link>
          </div>
        ) : done ? (
          <div className="text-center py-4">
            <CheckCircle2 className="h-12 w-12 text-green-600 mx-auto" />
            <h1 className="font-heading text-xl font-bold text-slate-900 mt-4">Password Direset</h1>
            <p className="text-sm text-slate-500 mt-2">Mengalihkan ke halaman login...</p>
          </div>
        ) : (
          <>
            <h1 className="font-heading text-2xl font-bold text-slate-900">Reset Password</h1>
            <p className="text-sm text-slate-500 mt-1">Masukkan password baru Anda.</p>
            {error && (
              <div className="mt-4 flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2.5 text-sm text-red-700" data-testid="reset-error">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" /> {error}
              </div>
            )}
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-600">Password Baru</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="pl-9" data-testid="reset-password-input" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-600">Konfirmasi Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="pl-9" data-testid="reset-confirm-input" />
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-green-700 hover:bg-green-800" data-testid="reset-submit-btn">
                {loading ? "Memproses..." : "Reset Password"}
              </Button>
            </form>
            <Link to="/login" className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mt-6">
              <ArrowLeft className="h-4 w-4" /> Kembali ke Login
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
